// useFocusContext — derives the "what should I do right now?" state
// for Home's Focus Card. Reads from sources already wired in App
// (scheduled meals, pantry, profile staples) and returns the FULL
// LIST of valid nudges in priority order so Home can render them
// as a swipeable + auto-rotating carousel.
//
// Priority order (first = first page in the carousel = most pressing):
//
//   1. almost-time     — scheduled meal in [now, now+3h]
//   2. use-this-up     — pantry item expiring in [now, now+48h]
//   3. restock         — at least one user-flagged staple has no
//                        in-stock pantry row (the "you're out of
//                        butter" nudge)
//   4. plan-tomorrow   — nothing scheduled for tomorrow's calendar
//                        day (regardless of pantry state — empty
//                        fridge + empty calendar is the BIGGEST
//                        gap, not a reason to skip the nudge)
//   5. ai-suggest      — appears ONLY when no other tier fires;
//                        "Pick something to make" is filler, not
//                        a notification, so it shouldn't add noise
//                        to a real-nudge stack
//
// Note: there is intentionally no "resume your cook" tier. Active
// cooks are owned by CookBanner (App-level), which sits above every
// tab with a live timer + RESUME tap — duplicating that here would
// just be noise.
//
// Thresholds are intentionally generous — false positives on the
// nudges are fine; they just suggest something the user might want
// to do anyway. Missing a real "your fridge is empty" moment is
// the failure mode we want to avoid.
//
// Returns: { tiers: [{ kind, ...payload }] }. Home's FocusCarousel
// maps over `tiers`, building one card per entry. Always non-empty —
// the worst case is a single ai-suggest page.
//
// Why a hook (vs inline in Home): Home is already social-feed
// heavy; isolating the focus logic keeps the component readable
// and lets us evolve the priority cascade without touching the
// view. Also lets tests target the cascade directly.

import { useMemo } from "react";
import { useScheduledMeals } from "./useScheduledMeals";
import { findIngredient } from "../data/ingredients";

const HOUR_MS = 60 * 60 * 1000;

export function useFocusContext({ userId, profile, pantry }) {
  // Pull scheduled meals for self for the next 48h. Tier 2 fires at
  // 3h; tier 5 (plan-tomorrow) walks the same window to detect
  // whether tomorrow's calendar day has anything in it.
  //
  // EGRESS GUARDRAIL: bucket `now` to the minute. useScheduledMeals
  // re-fetches whenever fromISO/toISO change identity. Reading a raw
  // Date.now() in render meant every Home re-render (auto-rotate
  // ticks, focus state changes, theme transitions) produced new ISO
  // strings → new query → new network roundtrip. Bucketing collapses
  // those into one query per minute. Window freshness is unaffected;
  // tier 2 fires at 3h precision, plan-tomorrow at calendar-day
  // precision — neither cares about sub-minute drift.
  const minuteBucket = Math.floor(Date.now() / 60000);
  const fromISO = useMemo(
    () => new Date(minuteBucket * 60000 - HOUR_MS).toISOString(),
    [minuteBucket],
  );
  const toISO = useMemo(
    () => new Date(minuteBucket * 60000 + 48 * HOUR_MS).toISOString(),
    [minuteBucket],
  );
  const { meals: scheduledMeals = [] } = useScheduledMeals(userId, {
    fromISO,
    toISO,
  }) || {};

  return useMemo(() => {
    const t = Date.now();
    // Pantry-off mode (profiles.kitchen_tracking === false): the
    // inventory-derived tiers (use-this-up, restock) are noise for a
    // user who deliberately doesn't track inventory. Schedule-derived
    // and generation tiers stay.
    const trackingOff = profile?.kitchen_tracking === false;
    const stapleIds = trackingOff
      ? []
      : Array.isArray(profile?.staple_canonicals)
        ? profile.staple_canonicals
        : [];
    const tiers = [];

    // 1. Scheduled meal landing within next 3 hours.
    const soonestMeal = (scheduledMeals || [])
      .filter((m) => {
        if (!m?.scheduled_for) return false;
        const at = new Date(m.scheduled_for).getTime();
        return at >= t - 15 * 60 * 1000 && at <= t + 3 * HOUR_MS;
      })
      .sort((a, b) =>
        new Date(a.scheduled_for).getTime() - new Date(b.scheduled_for).getTime(),
      )[0];
    if (soonestMeal) {
      tiers.push({
        kind: "almost-time",
        meal: soonestMeal,
        scheduledFor: new Date(soonestMeal.scheduled_for),
      });
    }

    // 2. Most-urgent pantry item expiring in next 48h.
    const urgentItem = (trackingOff ? [] : (pantry || []))
      .filter((p) => {
        if (!p?.expiresAt) return false;
        const at = new Date(p.expiresAt).getTime();
        return at >= t && at <= t + 48 * HOUR_MS;
      })
      .sort((a, b) =>
        new Date(a.expiresAt).getTime() - new Date(b.expiresAt).getTime(),
      )[0];
    if (urgentItem) {
      tiers.push({
        kind: "use-this-up",
        item: urgentItem,
        expiresAt: new Date(urgentItem.expiresAt),
      });
    }

    // 3. Restock — any user-flagged staple has no in-stock pantry row.
    //    A staple is "in stock" when at least one pantry row carries
    //    its canonical id with amount > 0. Items at amount = 0 (used
    //    up but not yet deleted) count as missing.
    if (stapleIds.length > 0) {
      const inStock = new Set(
        (pantry || [])
          .filter((p) => Number(p?.amount ?? 1) > 0 && p?.canonicalId)
          .map((p) => p.canonicalId),
      );
      const missing = stapleIds
        .filter((id) => !inStock.has(id))
        .map((id) => {
          const canon = findIngredient(id);
          return {
            id,
            name: canon?.name || id,
            emoji: canon?.emoji || null,
            canonical: canon || null,
          };
        });
      if (missing.length > 0) {
        tiers.push({ kind: "restock", missing });
      }
    }

    // 4. Plan tomorrow — nothing scheduled for the calendar day
    //    starting at midnight tonight (i.e. "tomorrow" in the user's
    //    local timezone). Doesn't gate on pantry being stocked: an
    //    empty kitchen + an empty calendar is the BIGGEST gap, not
    //    a reason to skip the nudge. The card adapts its copy based
    //    on whether the pantry has anything to cook from.
    const tomorrowStart = new Date();
    tomorrowStart.setHours(0, 0, 0, 0);
    tomorrowStart.setDate(tomorrowStart.getDate() + 1);
    const tomorrowEnd = new Date(tomorrowStart);
    tomorrowEnd.setDate(tomorrowEnd.getDate() + 1);
    const tomorrowMeals = (scheduledMeals || []).filter((m) => {
      if (!m?.scheduled_for) return false;
      const at = new Date(m.scheduled_for).getTime();
      return at >= tomorrowStart.getTime() && at < tomorrowEnd.getTime();
    });
    if (tomorrowMeals.length === 0) {
      const stocked = (pantry || []).length > 0;
      tiers.push({ kind: "plan-tomorrow", stocked });
    }

    // 5. Default — nothing else fired. ai-suggest is filler, not a
    //    notification, so it shouldn't share a stack with real
    //    nudges. Only push it when the carousel would otherwise
    //    be empty.
    if (tiers.length === 0) {
      tiers.push({ kind: "ai-suggest", hasStaples: stapleIds.length > 0 });
    }

    return { tiers };
  }, [scheduledMeals, pantry, profile?.staple_canonicals]);
}
